import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { LinkingOptions } from '@react-navigation/native';
import * as Linking from 'expo-linking';
import React, { useEffect, useLayoutEffect } from 'react';
import { Dimensions, Platform, StatusBar } from 'react-native';
import { ToastProvider } from 'react-native-toast-notifications';

import { CoreProviders } from '@components/CoreProviders';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { ServerErrorToast } from '@components/shared/ServerErrorToast';
import { AppStack } from '@navigation/app-stack';
import { AuthStack } from '@navigation/auth/auth-stack';
import { PaymentsStackParamsList } from '@navigation/payments/payments-stack';
import { ProjectsStackParamsList } from '@navigation/projects/projects-stack';
import { TasksStackParamsList } from '@navigation/tasks/tasks-stack';
import { ThemedNavigationContainer } from '@navigation/ThemedNavigationContainer';
import ProjectReport from '@screens/Projects/Report/ProjectReport';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useAuthContext } from '@src/context/authContext';
import MixpanelGlobalProperties from '@src/context/MixpanelGlobalProperties';
import { useMixpanel } from '@src/utils/mixpanel';
import { TASKTAG_APP_HOSTS } from '@utils/internalLinks';
import {
  isPasswordResetUrl,
  PASSWORD_RESET_PATH,
} from '@utils/passwordResetLink';
import { captureUtmParams } from '@utils/utmParams';

const prefix = Linking.createURL('/');

const taskPanelConfig: LinkingOptions<ReactNavigation.RootParamList>['config'] =
  {
    screens: {
      task: {
        path: '/',
        screens: {
          'task-details': '/',
          'activity-log': '/activity-log',
          members: '/members',
          files: {
            path: '/files',
            screens: {
              'all-task-files': 'all',
              'task-media': 'media',
            },
          },
        },
      },
      'task-edit': {
        path: '/edit',
        screens: {
          'task-form': '/',
        },
      },
      'task-copy-modal': {
        path: '/duplicate',
      },
    },
  };

const taskScreensWeb = {
  tasks: {
    path: '/:taskId?',
    ...taskPanelConfig,
  },
};

const globalActivityScreensWeb = {
  globalActivity: {
    path: '/',
  },
};

const taskScreensMobile: { [Value in keyof TasksStackParamsList]: string } = {
  'task-detail': '/:taskId', // ✅
  'task-create': '/:taskId/create',
  'task-edit': '/:taskId/edit', // ✅
  'task-search': '/:taskId/search',
  'task-members': '/:taskId/members', // ✅
  'task-member-role-select-modal': '/:taskId/member-role-select-modal',
  'task-copy-modal': '/:taskId/duplicate', // ✅
  'task-activity-log': '/:taskId/activity-log', // ✅
  'task-add-members-stack': '/:taskId/add-members-stack',
  'task-details-modal': '/:taskId/details-modal',
};

const paymentsScreensWeb = {
  'pick-plans': {
    path: '/',
  },
  'create-subscription': {
    path: '/new/:planId',
  },
  'subscription-created': {
    path: '/complete/:teamUUId/:productId',
  },
};
const myaccountScreensWeb = {
  tabs: {
    path: '/',
    screens: {
      'my-profile': {
        path: '/my-profile',
        screens: {
          'team-details': {
            path: '/:teamUUId',
          },
          'team-members': {
            path: '/:teamUUId/members',
          },
          'choose-plan': {
            path: '/:teamUUId/choose-plan',
          },
          'team-analytics': {
            path: '/:teamUUId/analytics',
          },
          'team-member-role-select-modal': {
            path: '/team-member-role-select-modal',
          },
          'create-join-team-modal': {
            path: '/:teamUUId/create-join-team-modal',
          },
          'assign-new-owner': {
            path: '/:teamUUId/assign-new-owner',
          },
          'add-team-members-stack': {
            path: '/:teamUUId/add-team-members-stack',
          },
          'team-subscription': {
            path: '/:teamUUId/subscription',
          },
        },
      },
      'subscription-plans': '/subscription-plans',
      settings: '/settings',
    },
  },
  'my-profile-mobile': '/mobile-profile',
  subscriptionPlanDetail: {
    path: 'subscriptionPlanDetail/:teamUUId',
    screens: {
      TeamDetails: '/TeamDetails',
      TeamInvoice: '/TeamInvoice',
      TeamMembers: '/TeamMembers',
    },
  },

  'subscription-created': {
    path: '/complete/:teamUUId/:productId',
  },
  'subscription-edit': {
    path: '/updated/:teamUUId/:productId',
  },
};

const paymentsScreensMobile: {
  [Value in keyof PaymentsStackParamsList]: string;
} = {
  'upgrade-plus': '/',
};

const teamsScreensWeb = {
  'my-teams': {
    path: '/',
  },
  'team-details': {
    path: '/:teamUUId',
  },
  'team-members': {
    path: '/:teamUUId/members',
  },
  'team-analytics': {
    path: '/:teamUUId/analytics',
  },
  'team-subscription': {
    path: '/:teamUUId/subscription',
  },
  'add-team-members-stack': {
    path: '/:teamUUId/add-members',
    screens: {
      'add-members': '/',
      'edit-new-member-roles': '/roles',
    },
  },
};

const projectScreensWeb = {
  tabs: {
    path: '/',
    screens: {
      allprojects: '/allprojects',
    },
  },
  details: {
    path: '/:projectUUId',
    screens: {
      'project-details': '/',
      'activity-log': '/activity-log',
      checklist: '/checklist',
      'project-report': '/project-report/:projectUUId',
      tasks: {
        path: '/tasks/:taskId?',
        ...taskPanelConfig,
      },
      members: '/members',
      files: {
        path: '/files',
        screens: {
          'all-project-files': '/all',
          'project-media': '/media',
          'project-task-files': '/tasks',
        },
      },
    },
  },
  new: {
    path: 'create',
    screens: { 'create-project-form': '/' },
  },
  edit: {
    path: '/:projectUUId/edit',
    screens: { 'create-project-form': '/form' },
  },
  duplicate: {
    path: '/:projectUUId/duplicate',
    screens: { 'create-project-form': '/form' },
  },
  colorSelect: {
    path: '/:projectUUId/colorSelect',
    screens: { 'create-project-form': '/form' },
  },
};
const projectScreensMobile: {
  [Value in keyof ProjectsStackParamsList]: string;
} = {
  'project-details': '/:projectUUId',
  'project-create': '/create',
  'project-edit': '/:projectUUId/edit',
  'project-search': '/search',
  'project-members': '/:projectUUId/members',
  'project-member-role-select-modal': '/:projectUUId/member-role-select-modal',
  'project-copy-modal': '/:projectUUId/duplicate',
  'project-activity-log': '/:projectUUId/activity-log',
  'project-add-members-stack': '/:projectUUId/add-members-stack',
  'project-details-modal': '/:projectUUId/details-modal',
  'create-phase-stack': '/create-phase',
  'project-form-stack': '/form-stack',
  'project-tasks': '/:projectUUId/tasks/:taskId?',
};

const contactPanelConfig: LinkingOptions<ReactNavigation.RootParamList>['config'] =
  {
    screens: {
      contact: {
        path: '/',
        screens: {
          'contact-info': '/profile',
          'contact-projects': '/projects',
          'contact-tasks': '/tasks',
        },
      },
      'contact-task-details': {
        path: '/task',
      },
    },
  };

const contactScreensWeb = {
  contacts: {
    path: '/:userId?',
    ...contactPanelConfig,
  },
};

const appConfig: LinkingOptions<ReactNavigation.RootParamList>['config'] = {
  screens: {
    'password-reset': PASSWORD_RESET_PATH,
    'log-in': 'login/:token/:type?/:id',
    register: Platform.OS === 'web' ? 'register' : 'register/:token',
    'chats-stack': {
      screens: {
        'chat-detail': 'chat/:id/:messageCursor?',
      },
    },
    home: {
      path: '/',
      screens: {
        'chats-home': 'chat',
        ...(Platform.OS !== 'web' && {
          'projects-home': 'projects/all-projects',
        }),
        ...(Platform.OS !== 'web' && { 'tasks-home': 'task-list' }),
      },
    },
    'projects-stack': {
      path: 'projects',
      screens: Platform.OS === 'web' ? projectScreensWeb : projectScreensMobile,
    },
    'tasks-stack': {
      path: 'tasks',
      screens: Platform.OS === 'web' ? taskScreensWeb : taskScreensMobile,
    },
    'globalActivity-stack': {
      path: 'globalActivity',
      screens: globalActivityScreensWeb,
    },
    'contacts-stack': {
      path: 'contacts',
      screens: contactScreensWeb,
    },
    'payments-stack': {
      path: 'payments',
      screens:
        Platform.OS === 'web' ? paymentsScreensWeb : paymentsScreensMobile,
    },
    'my-account': {
      path: 'my-account',
      screens: myaccountScreensWeb,
    },
    'teams-stack': {
      path: 'teams',
      screens: teamsScreensWeb,
    },
  },
};

const linking: LinkingOptions<ReactNavigation.RootParamList> = {
  prefixes: [
    prefix,
    'com.eloveit.tasktag:///',
    'tasktag:///',
    // Derived from the same constant the in-app link handler matches on, so a
    // host added later cannot be picked up here but missed there.
    ...TASKTAG_APP_HOSTS.map((host) => `https://${host}/`),
  ],
  config: appConfig,
  // TA-11134: on native, password-reset links are routed by authContext
  // instead — it must also work while signed in, where the route does not
  // exist on AppStack. Letting linking dispatch it too would race that.
  ...(Platform.OS !== 'web' && {
    subscribe: (listener: (url: string) => void) => {
      const subscription = Linking.addEventListener('url', ({ url }) => {
        if (!isPasswordResetUrl(url)) listener(url);
      });
      return () => subscription.remove();
    },
  }),
  // Prevent React Navigation from auto-navigating on register deeplinks
  // after the user has authenticated. The registration flow stores deeplink
  // params in AsyncStorage and handles navigation manually.
  getInitialURL: async () => {
    const url = await Linking.getInitialURL();
    // TA-11134: see `subscribe` — authContext owns native reset links.
    if (Platform.OS !== 'web' && isPasswordResetUrl(url)) {
      return null;
    }
    if (url?.includes('/register/')) {
      return null;
    }
    // Inviter deep link (com.eloveit.tasktag:///?inviterId=...) — handled
    // manually post-login in Chats.tsx, so block RN auto-navigation.
    if (url?.includes('inviterId=')) {
      return null;
    }
    // The email CTA generates /my-profile/{uuid}/choose-plan but the deep link
    // config nests choose-plan under my-account/my-profile. Rewrite it so RN
    // routes to PlanSelect correctly.
    if (url) {
      const m = url.match(
        /(https?:\/\/[^/]+)\/my-profile\/([^/?#]+)\/choose-plan/
      );
      if (m) return `${m[1]}/my-account/my-profile/${m[2]}/choose-plan`;
    }
    return url;
  },
};

// switch between the <AppStack /> and an <AuthStack /> based on user's auth

export const AppNavigator: React.FC<{ startTime?: number }> = ({
  startTime,
}) => {
  const { token, loading, passwordResetPending } = useAuthContext();
  const { trackEvent } = useMixpanel();
  const hasTrackedDashboard = React.useRef(false);

  useEffect(() => {
    if (token && !hasTrackedDashboard.current) {
      hasTrackedDashboard.current = true;
      trackEvent(MIXPANEL_EVENTS.ENTERED_DASHBOARD);
    }
  }, [token, trackEvent]);

  useEffect(() => {
    if (!loading && startTime) {
      const elapsedTime = performance.now() - startTime;
      if (elapsedTime > 3000) {
        trackEvent(MIXPANEL_EVENTS.PAGE_LOAD_TIME_EXCEEDED, {
          load_time_ms: elapsedTime,
        });
      }
    }
  }, [loading, startTime, trackEvent]);

  // useLayoutEffect runs before child useEffect hooks (e.g. Mixpanel init),
  // ensuring UTMs are stored before any events fire on the first render.
  useLayoutEffect(() => {
    if (Platform.OS !== 'web') return;
    captureUtmParams();
  }, []);

  if (Platform.OS === 'web') {
    const path = window.location.pathname;
    if (path.startsWith('/account_invitations/')) {
      const code = path.substring('/account_invitations/'.length);
      window.sessionStorage.setItem('invitation_token', code);
      // this from when user signup -> create team -> invite user ->
      // invited user click invitation link from email inbox, so I put it here -- april TA-5972
      window.sessionStorage.setItem('link_type', 'team');
      window.sessionStorage.setItem('task_or_project_id', '');
    } else if (path.startsWith('/chat/')) {
      // for click chat notification
      try {
        // Split the path into parts
        const [...rest] = path.split('/').filter((part) => part);

        // Ensure that we have enough parts to extract the parameters
        const parts = rest;

        const chatId = parts[1];
        const messageId = parts[2];

        chatId && window.sessionStorage.setItem('notification_chat_id', chatId);
        messageId &&
          window.sessionStorage.setItem('notification_message_id', messageId);
        chatId &&
          window.sessionStorage.setItem('notification_open_chat', 'true');
      } catch (e) {
        console.log(e);
      }
    } else {
      const queryParameters = new URLSearchParams(window.location.search);
      const isGroupInviteLink = path.startsWith('/invite/group');
      const invitationToken =
        queryParameters.get('invitation_token') ??
        queryParameters.get('token') ??
        '';
      const type =
        queryParameters.get('type') ?? (isGroupInviteLink ? 'chat' : '');
      const id = queryParameters.get('id') ?? '';
      if (invitationToken) {
        window.sessionStorage.setItem('invitation_token', invitationToken);
        window.sessionStorage.setItem('link_type', type);
        window.sessionStorage.setItem('task_or_project_id', id);
        if (type === 'chat') {
          window.sessionStorage.setItem(
            'pending_chat_invitation_token',
            invitationToken
          );
          if (id) {
            window.sessionStorage.setItem('pending_chat_invitation_id', id);
          }
        }
      }
    }
  }

  const getStack = () => {
    if (Platform.OS === 'web') {
      const getInitialURL = window.location.href ?? '';
      const getInitialURLParts = getInitialURL.split('=');
      const id = getInitialURLParts[getInitialURLParts?.length - 1];
      if (
        getInitialURL.includes('/project-report') &&
        getInitialURL.includes('uuid') &&
        id
      ) {
        return (
          <CoreProviders>
            <ToastProvider>
              <ServerErrorToast />
              <ProjectReport
                route={{
                  params: {
                    tokenizedUUID: id,
                  },
                }}
              />
            </ToastProvider>
          </CoreProviders>
        );
      }
      if (
        getInitialURL.includes('/project-report') &&
        getInitialURL.includes('projectUUId') &&
        id
      ) {
        return (
          <CoreProviders>
            <ToastProvider>
              <ServerErrorToast />
              <ProjectReport
                route={{
                  params: {
                    projectUUId: id,
                  },
                }}
              />
            </ToastProvider>
          </CoreProviders>
        );
      }
    }

    if (loading) {
      return (
        <ActivityIndicatorLoading
          top={(Dimensions.get('window').height - 40) / 2}
        />
      );
    } else {
      return (
        <CoreProviders>
          <ToastProvider
            offsetTop={
              Platform.OS === 'android' ? (StatusBar.currentHeight ?? 0) : 0
            }
            renderType={{
              'custom-transparent': (toast) => <>{toast.message}</>,
            }}>
            {/* Navigation-level sheet host: sits inside NavigationContainer but
                above every navigator, so sheets that use it render over the
                screen headers (their backdrop dims the whole screen) while
                their content still resolves a navigation object. Screens with
                their own BottomSheetModalProvider keep the local host —
                TA-10488. */}
            <BottomSheetModalProvider>
              {/* TA-2593 (web): a reset link opened while signed in renders
                  on AuthStack without ending the session first. Native
                  (TA-11134) opens it as a modal inside AppStack instead, so
                  the user's screens and drafts survive. */}
              {token && !passwordResetPending ? <AppStack /> : <AuthStack />}
              <MixpanelGlobalProperties />
              <ServerErrorToast />
            </BottomSheetModalProvider>
          </ToastProvider>
        </CoreProviders>
      );
    }
  };

  return (
    <ThemedNavigationContainer
      linking={linking}
      documentTitle={{
        enabled: true,
        formatter: (options) => options?.title || 'TaskTag App',
      }}>
      {getStack()}
    </ThemedNavigationContainer>
  );
};
